import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

/**
 * Touch-friendly tabs components with:
 * - Minimum 44px tab height for touch targets (WCAG 2.5.5)
 * - Adequate spacing between tabs
 * - Visual feedback for touch interactions
 */

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Base styles - increased h-10 to h-12 for touch-friendly height
      "inline-flex h-12 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      // Touch-friendly spacing between tabs
      "gap-1",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base styles with touch-friendly sizing
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm",
      // Touch-friendly padding - increased from px-3 py-1.5 for 44px minimum
      "px-4 py-2.5 min-h-[40px]",
      // Typography
      "text-sm font-medium",
      // Ring offset for focus
      "ring-offset-background",
      // Transitions
      "transition-all",
      // Focus states
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      // Disabled states
      "disabled:pointer-events-none disabled:opacity-50",
      // Active states
      "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      // Touch-friendly enhancements
      "touch-action-manipulation select-none",
      // Active press feedback
      "active:scale-[0.98] transition-transform duration-100",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // Base styles with adequate spacing
      "mt-3 ring-offset-background",
      // Focus states
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
